<template lang="pug">
    .reservation-overview
        //- Layout control bar
        .reservation-overview__layout-bar
            .reservation-overview__layout-bar-left
                span.reservation-overview__fullscreen-toggle(@click.stop.prevent="toggleFullscreen")
                    i.fas.el-icon-fa-expand
                el-button(
                    v-if="canCreateReservation"
                    type="primary"
                    size="small"
                    icon="el-icon-plus"
                    @click="createReservation"
                ) {{ $t('New Reservation') }}

            .reservation-overview__layout-bar-right
                .reservation-overview__layout-controls
                    //- Direction group with label
                    .reservation-overview__layout-group
                        .reservation-overview__layout-group-buttons
                            el-tooltip(:content="$t('Horizontal layout')" placement="bottom")
                                el-button(
                                    size="mini"
                                    :type="layoutDirection === 'horizontal' ? 'primary' : 'default'"
                                    :plain="layoutDirection !== 'horizontal'"
                                    circle
                                    @click="layoutDirection = 'horizontal'"
                                )
                                    i.el-icon-fa-table-columns.fas
                            el-tooltip(:content="$t('Vertical layout')" placement="bottom")
                                el-button(
                                    size="mini"
                                    :type="layoutDirection === 'vertical' ? 'primary' : 'default'"
                                    :plain="layoutDirection !== 'vertical'"
                                    circle
                                    @click="layoutDirection = 'vertical'"
                                )
                                    i.el-icon-fa-table-columns.fas(style="display:inline-block; transform: rotate(90deg)")
                        .reservation-overview__layout-group-label {{ $t('Arrangement') }}

                    .reservation-overview__layout-divider

                    //- Order group with label
                    .reservation-overview__layout-group
                        .reservation-overview__layout-group-buttons
                            el-tooltip(:content="$t('Panel order: first panel shown left / top')" placement="bottom")
                                el-radio-group(v-model="panelOrder" size="mini")
                                    el-radio-button(label="plan-first") {{ $t('Floor plan') }}
                                    el-radio-button(label="list-first") {{ $t('List') }}
                        .reservation-overview__layout-group-label {{ $t('Show first') }}

                el-tooltip(v-if="canAccessSettings" :content="$t('Settings')" placement="bottom")
                    el-button.reservation-overview__settings-button(
                        size="small"
                        circle
                        @click="openSettings"
                    )
                        i.el-icon-fa-cog.fas


        //- Split panels
        .reservation-overview__split(ref="split" :class="splitClasses")
            //- Left: Floor Plan
            .reservation-overview__left(ref="leftPanel" :class="leftPanelClasses" :style="leftPanelStyle")
                el-button.reservation-overview__panel-expand(
                    v-if="minimizedPanel === 'left' && layoutDirection === 'horizontal'"
                    size="mini"
                    circle
                    @click="toggleMinimize('left')"
                )
                    i.el-icon-fa-chevron-right.fas
                transition(name="panel-collapse")
                    .reservation-overview__left-content(v-show="minimizedPanel !== 'left'")
                        floor-plan(
                            :drop-active="isDragging"
                            :show-collapse="layoutDirection === 'horizontal'"
                            :minimized-panel="minimizedPanel"
                            @reservation-drop="onReservationDrop"
                            @toggle-collapse="toggleMinimize('left')"
                        )

            //- Drag handle between the panels (mouse, pen and touch)
            el-tooltip(
                v-if="isSplitResizable"
                :content="$t('Drag to resize, double click to reset')"
                placement="top"
                :disabled="isResizing"
            )
                .reservation-overview__resizer(
                    role="separator"
                    tabindex="0"
                    aria-orientation="vertical"
                    :aria-valuenow="splitPercentage"
                    aria-valuemin="20"
                    aria-valuemax="80"
                    :aria-label="$t('Resize panels')"
                    @pointerdown="startResize"
                    @dblclick="resetSplitRatio"
                    @keydown.left.prevent="nudgeSplitRatio(-1)"
                    @keydown.right.prevent="nudgeSplitRatio(1)"
                    @keydown.enter.prevent="resetSplitRatio"
                )
                    .reservation-overview__resizer-grip

            //- Right: List or Timeline
            .reservation-overview__right(ref="rightPanel" :class="rightPanelClasses" :style="rightPanelStyle")
                .reservation-overview__right-header
                    el-tooltip(
                        v-if="layoutDirection === 'horizontal'"
                        :content="minimizedPanel === 'right' ? $t('Show list') : $t('Hide list')"
                        placement="left"
                    )
                        el-button.reservation-overview__panel-toggle.reservation-overview__panel-toggle--right(
                            size="mini"
                            circle
                            @click="toggleMinimize('right')"
                        )
                            i(:class="minimizedPanel === 'right' ? 'el-icon-fa-chevron-left fas' : 'el-icon-fa-chevron-right fas'")

                    .reservation-overview__toggle
                        el-radio-group(v-model="rightTab" size="mini")
                            el-radio-button(label="list")
                                i.el-icon-fa-list.fas
                                |  {{ $t('List') }}
                            el-radio-button(label="timeline")
                                i.el-icon-fa-clock.far
                                |  {{ $t('Timeline') }}

                    .reservation-overview__right-header-spacer(v-if="rightTab === 'timeline'")
                    template(v-if="rightTab === 'timeline'")
                        el-date-picker(
                            v-model="timelineDate"
                            type="date"
                            format="dd.MM.yyyy"
                            value-format="yyyy-MM-dd"
                            :clearable="false"
                            size="mini"
                            style="width: 140px"
                        )
                        .reservation-overview__timeline-info
                            span {{ $t('Reservations') }}: {{ timelineReservationCount }}
                            span(style="margin-left: 12px") {{ $t('Guests') }}: {{ timelineGuestCount }}

                transition(name="panel-collapse")
                    .reservation-overview__right-content(v-show="minimizedPanel !== 'right'")
                        reservations-list(
                            v-if="rightTab === 'list'"
                            :embedded="true"
                            @drag-start="onDragStart"
                            @drag-end="onDragEnd"
                        )
                        timeline-view(v-else)
</template>

<i18n>
{
    "de": {
        "List": "Liste",
        "Timeline": "Timeline",
        "New Reservation": "Neue Reservierung",
        "Table assigned": "Tisch zugewiesen",
        "assigned to table": "zugewiesen an Tisch",
        "Horizontal layout": "Horizontales Layout",
        "Vertical layout": "Vertikales Layout",
        "Panel order: first panel shown left / top": "Reihenfolge: erstes Panel links / oben",
        "Floor plan": "Tischplan",
        "Show floor plan": "Tischplan einblenden",
        "Hide floor plan": "Tischplan ausblenden",
        "Show list": "Liste einblenden",
        "Hide list": "Liste ausblenden",
        "Arrangement": "Anordnung",
        "Show first": "Zuerst anzeigen",
        "Settings": "Einstellungen",
        "Reservations": "Reservierungen",
        "Guests": "Gäste",
        "Resize panels": "Bereiche anpassen",
        "Drag to resize, double click to reset": "Ziehen zum Anpassen, Doppelklick zum Zurücksetzen",
        "Upcoming booking": "Achtung!",
        "Next booking arrives in {minutes} min for table {name}.": "Bei Tisch {name} kommt in {minutes} Min. die nächste Buchung!"
    },
    "en": {
        "Next booking arrives in {minutes} min for table {name}.": "Next booking arrives in {minutes} min for table {name}."
    }
}
</i18n>

<script>
import _ from 'lodash';
import { format } from 'date-fns';
import router from '@/router';
import { checkRestrictions } from '@/helpers';
import UserApi from '@/store/api/system/user';
import { useAuthStore } from '@/stores/auth';
import { useTableReservationStore, useTableReservationFloorPlanStore } from '@/stores';
import { toMinutes, findNextReservationForTable } from '@/extensions/tableReservation/store/utils';
import FloorPlan from './FloorPlan/index.vue';
import ReservationsList from './Reservations/List.vue';
import TimelineView from './Timeline/index.vue';

const MINIMUM_SPLIT_RATIO = 0.2;
const MAXIMUM_SPLIT_RATIO = 0.8;
const DEFAULT_SPLIT_RATIO = 0.5;
const KEYBOARD_SPLIT_STEP = 0.02;

export default {
    name: 'ReservationOverview',
    components: {
        FloorPlan: FloorPlan,
        ReservationsList: ReservationsList,
        TimelineView: TimelineView
    },
    data: function () {
        return {
            rightTab: 'list',
            isDragging: false,
            draggingReservation: null,
            layoutDirection: 'horizontal', // 'horizontal' | 'vertical'
            panelOrder: 'plan-first',      // 'plan-first' | 'list-first'
            minimizedPanel: null,          // null | 'left' | 'right' (horizontal only)
            splitRatio: DEFAULT_SPLIT_RATIO, // share of the horizontal split taken by the first panel
            isResizing: false,
            resizeState: null,
            preferencesReady: false,
            persistOverviewPreferencesDebounced: null,
            warnedPairs: new Set(),
            nextBookingTimer: null
        };
    },
    created: function () {
        this.persistOverviewPreferencesDebounced = _.debounce(() => {
            this.persistOverviewPreferences();
        }, 250);

        this.applyStoredOverviewPreferences();

        if (this.$route.query.tab === 'timeline') {
            this.rightTab = 'timeline';
        }
    },
    beforeDestroy: function () {
        this.stopResize();

        if (this.persistOverviewPreferencesDebounced) {
            this.persistOverviewPreferencesDebounced.cancel();
        }
        if (this.nextBookingTimer) {
            clearInterval(this.nextBookingTimer);
            this.nextBookingTimer = null;
        }
    },
    watch: {
        layoutDirection: function (value, oldValue) {
            this.minimizedPanel = null;
            if (!this.preferencesReady || value === oldValue) {
                return;
            }

            this.persistOverviewPreferencesDebounced();
        },
        panelOrder: function (value, oldValue) {
            if (!this.preferencesReady || value === oldValue) {
                return;
            }

            this.persistOverviewPreferencesDebounced();
        }
    },
    computed: {
        store: function () {
            return useTableReservationStore();
        },
        floorPlanStore: function () {
            return useTableReservationFloorPlanStore();
        },
        splitClasses: function () {
            return {
                'reservation-overview__split--vertical': this.layoutDirection === 'vertical',
                'reservation-overview__split--reversed': this.panelOrder === 'list-first',
                'reservation-overview__split--resizable': this.isSplitResizable,
                'reservation-overview__split--resizing': this.isResizing
            };
        },
        isSplitResizable: function () {
            return this.layoutDirection === 'horizontal' && this.minimizedPanel === null;
        },
        splitPercentage: function () {
            return Math.round(this.splitRatio * 100);
        },
        leftPanelStyle: function () {
            if (!this.isSplitResizable) {
                return {};
            }

            return { flex: this.splitRatio + ' 1 0%' };
        },
        rightPanelStyle: function () {
            if (!this.isSplitResizable) {
                return {};
            }

            return { flex: (1 - this.splitRatio) + ' 1 0%' };
        },
        leftPanelClasses: function () {
            return {
                'reservation-overview__left--minimized': this.minimizedPanel === 'left',
                'reservation-overview__left--expanded': this.minimizedPanel === 'right'
            };
        },
        rightPanelClasses: function () {
            return {
                'reservation-overview__right--minimized': this.minimizedPanel === 'right',
                'reservation-overview__right--expanded': this.minimizedPanel === 'left'
            };
        },
        canCreateReservation: function () {
            return checkRestrictions(['ex:trsv:reservations'], 'create');
        },
        canAccessSettings: function () {
            return checkRestrictions(['ex:trsv:settings']);
        },
        timelineDate: {
            get: function () { return this.store.filters.date; },
            set: function (value) {
                this.store.setFilterDate(value).catch((error) => { this.showActionError(error); });
            }
        },
        timelineReservations: function () {
            return this.store.reservationsByDate(this.store.filters.date);
        },
        timelineReservationCount: function () {
            return this.timelineReservations.length;
        },
        timelineGuestCount: function () {
            return this.timelineReservations
                .filter((r) => { return r.status !== 'cancelled' && r.status !== 'no_show'; })
                .reduce((sum, r) => { return sum + r.partySize; }, 0);
        }
    },
    mounted: async function () {
        try {
            await this.store.ensureBoard();
        } catch (error) {
            this.showActionError(error);
        }

        this.checkUpcomingBookings();
        this.nextBookingTimer = setInterval(() => {
            this.checkUpcomingBookings();
        }, 60000);
    },
    methods: {
        showActionError: function (error) {
            this.$message({
                message: error.message || this.$t('Something went wrong'),
                type: 'error',
                duration: 5000
            });
        },
        applyStoredOverviewPreferences: function () {
            const overviewSettings = _.get(useAuthStore(), 'user.settings.ex.tableReservation.overview', {});
            const layoutDirection = _.get(overviewSettings, 'layoutDirection');
            const panelOrder = _.get(overviewSettings, 'panelOrder');
            const splitRatio = Number(_.get(overviewSettings, 'splitRatio'));

            if (['horizontal', 'vertical'].includes(layoutDirection)) {
                this.layoutDirection = layoutDirection;
            }

            if (['plan-first', 'list-first'].includes(panelOrder)) {
                this.panelOrder = panelOrder;
            }

            if (Number.isFinite(splitRatio)) {
                this.splitRatio = this.clampSplitRatio(splitRatio);
            }

            this.preferencesReady = true;
        },
        buildOverviewUserSettings: function () {
            let settings = _.get(useAuthStore(), 'user.settings', {});
            settings = settings ? _.cloneDeep(settings) : {};

            _.set(settings, 'ex.tableReservation.overview', {
                layoutDirection: this.layoutDirection,
                panelOrder: this.panelOrder,
                splitRatio: this.splitRatio
            });

            return settings;
        },
        persistOverviewPreferences: async function () {
            const authStore = useAuthStore();
            const userId = _.get(authStore, 'user.id');

            if (!userId) {
                return;
            }

            const settings = this.buildOverviewUserSettings();
            authStore.updateUserSettings(settings);

            try {
                const result = await UserApi.update({ id: userId, settings: settings });
                authStore.updateUserSettings(_.get(result, 'settings', settings));
            } catch (error) {
                this.showActionError(error);
            }
        },
        toggleFullscreen: function () {
            document.querySelector('body').classList.toggle('show-navigation');
        },
        openSettings: function () {
            router.push({ name: 'table-reservations-settings' });
        },
        createReservation: function () {
            router.push({ name: 'table-reservation-form' });
        },
        toggleMinimize: function (panel) {
            this.minimizedPanel = this.minimizedPanel === panel ? null : panel;
        },
        clampSplitRatio: function (ratio) {
            return Math.min(MAXIMUM_SPLIT_RATIO, Math.max(MINIMUM_SPLIT_RATIO, ratio));
        },
        startResize: function (event) {
            if (!this.isSplitResizable || this.resizeState) {
                return;
            }

            const leftPanel = this.$refs.leftPanel;
            const rightPanel = this.$refs.rightPanel;

            if (!leftPanel || !rightPanel) {
                return;
            }

            // Prevents text selection, scrolling and the synthetic mouse events on touch devices.
            event.preventDefault();

            const handle = event.currentTarget;

            if (handle.setPointerCapture && event.pointerId !== undefined) {
                handle.setPointerCapture(event.pointerId);
            }

            this.resizeState = {
                handle: handle,
                pointerId: event.pointerId,
                startPosition: event.clientX,
                startRatio: this.splitRatio,
                // The space the two panels share — gaps and the handle itself are already excluded.
                availableSize: Math.max(leftPanel.offsetWidth + rightPanel.offsetWidth, 1)
            };
            this.isResizing = true;

            window.addEventListener('pointermove', this.onResizeMove);
            window.addEventListener('pointerup', this.stopResize);
            window.addEventListener('pointercancel', this.stopResize);
        },
        onResizeMove: function (event) {
            if (!this.resizeState || event.pointerId !== this.resizeState.pointerId) {
                return;
            }

            // In the reversed order the first panel sits on the right, so the drag direction flips.
            const direction = this.panelOrder === 'list-first' ? -1 : 1;
            const delta = (event.clientX - this.resizeState.startPosition) * direction;

            this.splitRatio = this.clampSplitRatio(
                this.resizeState.startRatio + delta / this.resizeState.availableSize
            );
        },
        stopResize: function (event) {
            if (!this.resizeState) {
                return;
            }

            // A second finger lifting must not end the drag of the first one
            if (event && event.pointerId !== this.resizeState.pointerId) {
                return;
            }

            const handle = this.resizeState.handle;
            const pointerId = this.resizeState.pointerId;

            if (handle && handle.releasePointerCapture && pointerId !== undefined && handle.hasPointerCapture(pointerId)) {
                handle.releasePointerCapture(pointerId);
            }

            window.removeEventListener('pointermove', this.onResizeMove);
            window.removeEventListener('pointerup', this.stopResize);
            window.removeEventListener('pointercancel', this.stopResize);

            this.resizeState = null;
            this.isResizing = false;

            this.notifyPanelResize();

            if (this.preferencesReady) {
                this.persistOverviewPreferencesDebounced();
            }
        },
        nudgeSplitRatio: function (steps) {
            if (!this.isSplitResizable) {
                return;
            }

            const direction = this.panelOrder === 'list-first' ? -1 : 1;
            this.splitRatio = this.clampSplitRatio(this.splitRatio + steps * direction * KEYBOARD_SPLIT_STEP);

            this.notifyPanelResize();

            if (this.preferencesReady) {
                this.persistOverviewPreferencesDebounced();
            }
        },
        resetSplitRatio: function () {
            this.splitRatio = DEFAULT_SPLIT_RATIO;

            this.notifyPanelResize();

            if (this.preferencesReady) {
                this.persistOverviewPreferencesDebounced();
            }
        },
        notifyPanelResize: function () {
            // The timeline recalculates its scrollbar width on window resize only.
            this.$nextTick(() => {
                window.dispatchEvent(new Event('resize'));
            });
        },
        onDragStart: function (reservation) {
            this.isDragging = true;
            this.draggingReservation = reservation;
            this.minimizedPanel = null; // always show both panels during drag
        },
        onDragEnd: function () {
            this.isDragging = false;
            this.draggingReservation = null;
        },
        onReservationDrop: async function (payload) {
            const tableId = payload.tableId;
            const reservationId = payload.reservationId;

            // Find the table name for the message
            const table = this.floorPlanStore.tables.find((t) => { return t.id === tableId; });
            const tableName = table ? table.name : tableId;

            // Find the reservation for the message
            const reservation = this.store.reservations.find((r) => { return r.id === reservationId; });
            const guestName = reservation ? reservation.guestName : '';

            try {
                await this.store.assignTable(reservationId, tableId);
                this.$message({
                    message: guestName + ' ' + this.$t('assigned to table') + ' ' + tableName,
                    type: 'success',
                    duration: 3000
                });
            } catch (error) {
                this.showActionError(error);
            } finally {
                this.isDragging = false;
                this.draggingReservation = null;
            }
        },
        checkUpcomingBookings: function () {
            const today = format(new Date(), 'yyyy-MM-dd');
            if (this.store.filters.date !== today) {
                this.warnedPairs.clear();
                return;
            }

            const nowMins = toMinutes(format(new Date(), 'HH:mm'));
            const all = this.store.reservationsByDate(today);
            const activePairKeys = new Set();

            all.forEach((res) => {
                if (res.status !== 'seated') return;
                const next = findNextReservationForTable(res, all);
                if (!next) return;
                const diff = toMinutes(next.time) - nowMins;
                if (diff < 0 || diff > 15) return;

                const key = res.id + '|' + next.id;
                activePairKeys.add(key);
                if (this.warnedPairs.has(key)) return;
                this.warnedPairs.add(key);

                const tables = this.floorPlanStore.tables || [];
                const tableNames = (res.tableIds || []).map((id) => {
                    const t = tables.find((tt) => { return String(tt.id) === String(id); });
                    return t ? t.name : id;
                }).join(', ');

                this.$alert(
                    this.$t('Next booking arrives in {minutes} min for table {name}.', { minutes: diff, name: tableNames }),
                    this.$t('Upcoming booking'),
                    { confirmButtonText: this.$t('OK'), type: 'warning' }
                ).catch(() => {});
            });

            this.warnedPairs.forEach((key) => {
                if (!activePairKeys.has(key)) this.warnedPairs.delete(key);
            });
        }
    }
};
</script>

<style lang="sass" scoped>
@import '~@/assets/sass/variables'

.reservation-overview
    &__layout-bar
        display: flex
        justify-content: space-between
        padding: 0 1rem
        padding-top: 12px
        align-items: center
        margin-bottom: 16px

    &__layout-bar-left
        display: flex
        align-items: center
        gap: 12px

    &__layout-bar-right
        display: flex
        align-items: center
        gap: 12px

    &__fullscreen-toggle
        font-size: 1.4rem
        color: darken($color-skeleton, 40%)
        display: flex
        align-items: center
        cursor: pointer
        &:hover,
        &:focus
            color: $--color-primary

    &__settings-button
        font-size: 1rem
        color: #909399

    &__layout-controls
        display: flex
        align-items: center
        gap: 4px

    &__layout-divider
        width: 1px
        height: 16px
        background: #dcdfe6
        margin: 0 4px

    &__layout-group
        display: flex
        flex-direction: row
        align-items: center
        gap: 6px

        &-buttons
            display: flex
            align-items: center
            gap: 4px

        &-label
            font-size: 10px
            color: #909399
            white-space: nowrap
            line-height: 1
            order: -1

    &__split
        display: flex
        gap: 12px
        padding: 0 1rem
        min-height: calc(100vh - 220px)

        &--vertical
            flex-direction: column
            min-height: auto

        &--reversed
            flex-direction: row-reverse

        &--vertical.reservation-overview__split--reversed
            flex-direction: column-reverse

        //- The drag handle provides the spacing between the panels itself
        &--resizable
            gap: 0

        &--resizing
            user-select: none
            cursor: col-resize

            .reservation-overview__left,
            .reservation-overview__right
                transition: none

    &__resizer
        flex: 0 0 16px
        align-self: stretch
        display: flex
        align-items: center
        justify-content: center
        cursor: col-resize
        outline: none
        border-radius: 6px
        touch-action: none
        -webkit-tap-highlight-color: transparent

        &:hover .reservation-overview__resizer-grip,
        &:focus .reservation-overview__resizer-grip
            background: $--color-primary
            height: 64px

        &:focus
            box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.25)

        &-grip
            width: 4px
            height: 40px
            border-radius: 2px
            background: #dcdfe6
            transition: background 0.2s ease, height 0.2s ease
            pointer-events: none

    &__split--resizing &__resizer-grip
        background: $--color-primary
        height: 64px

    //- Touch devices get a wider target and a permanently visible grip
    @media (pointer: coarse)
        &__resizer
            flex: 0 0 28px

            .reservation-overview__resizer-grip
                width: 6px
                height: 72px
                background: #c0c4cc

    &__left
        flex: 1 1 50%
        min-width: 0
        overflow: auto
        position: relative
        background: #fff
        border: 1px solid #e4e7ed
        border-radius: 6px
        padding: 12px
        transition: flex-basis 0.3s ease, min-width 0.3s ease

        &--minimized
            flex: 0 0 40px
            min-width: 40px
            overflow: hidden
            padding: 4px

        &--expanded
            flex: 1 1 65%

        &-content
            height: 100%

    &__right
        flex: 1 1 50%
        min-width: 0
        overflow: auto
        display: flex
        flex-direction: column
        position: relative
        background: #fff
        border: 1px solid #e4e7ed
        border-radius: 6px
        padding: 12px
        transition: flex-basis 0.3s ease, min-width 0.3s ease

        &--minimized
            flex: 0 0 40px
            min-width: 40px
            overflow: hidden
            padding-inline: 6px

        &--expanded
            flex: 1 1 65%

    &__right-header
        display: flex
        align-items: center
        gap: 8px
        margin-bottom: 15px

    &__panel-expand
        display: block
        margin: 8px auto 0

    &__panel-toggle
        position: absolute
        top: 12px
        right: 10px
        z-index: 11

        &--right
            position: static

    &__toggle
        display: flex
        align-items: center

    &__right-header-spacer
        flex: 1

    &__timeline-info
        font-size: 0.85rem
        color: #606266
        white-space: nowrap

    &__right-content
        flex: 1
        min-height: 0
        overflow: auto

.panel-collapse-enter-active,
.panel-collapse-leave-active
    transition: opacity 0.2s ease

.panel-collapse-enter,
.panel-collapse-leave-to
    opacity: 0
</style>
